/**
 * Admin V3 — Billing / Invoices — Table component
 *
 * 9 columns: (expand) | Numéro | Émise le | Partenaire | Période
 *          | Échéance | Total TTC | Solde dû | Statut | Actions.
 *
 * Per-row expandable detail panel shows:
 *   - billing_cycle_key
 *   - booking_ids list (truncated)
 *   - line_items table
 *   - partner address + VAT number
 *   - amount paid + paid_at + notes
 *   - subtotal HT + TVA + total TTC breakdown
 *
 * Row Actions → /admin-v3/billing/invoices/:id (placeholder, future detail).
 *
 * Overdue rows: rose left-border for visual scan.
 *
 * NO mutation surface — no generate / issue / payment / void buttons.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  invoiceStatusMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const formatPeriod = (start, end, locale) => {
  if (!start && !end) return '—';
  if (start && end) return `${formatDate(start, locale)} → ${formatDate(end, locale)}`;
  return formatDate(start || end, locale);
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(9).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ invoice, locale }) => {
  const bookingIds = Array.isArray(invoice.booking_ids) ? invoice.booking_ids : [];
  const lineItems  = Array.isArray(invoice.line_items)  ? invoice.line_items  : [];
  const taxLabel = t('billing.invoices.detail.tax', locale).replace('{rate}', String(invoice.tax_rate ?? 0));

  return (
    <div className="space-y-3 bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      {/* Amount breakdown */}
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5">
        <div className="text-[12px]">
          <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
            {t('billing.invoices.detail.subtotal', locale)}
          </dt>
          <dd className="text-slate-700 font-medium">{currency(invoice.amount ?? 0)}</dd>
        </div>
        <div className="text-[12px]">
          <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{taxLabel}</dt>
          <dd className="text-slate-700 font-medium">{currency(invoice.tax_amount ?? 0)}</dd>
        </div>
        <div className="text-[12px]">
          <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
            {t('billing.invoices.detail.amountPaid', locale)}
          </dt>
          <dd className="text-emerald-700 font-medium">{currency(invoice.amount_paid ?? 0)}</dd>
        </div>
        {invoice.paid_at && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('billing.invoices.detail.paidAt', locale)}
            </dt>
            <dd className="text-slate-700">{formatDate(invoice.paid_at, locale)}</dd>
          </div>
        )}
      </dl>

      {/* Cycle + partner extras */}
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {invoice.billing_cycle_key && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('billing.invoices.detail.cycle', locale)}
            </dt>
            <dd className="text-slate-700 font-mono break-all">{invoice.billing_cycle_key}</dd>
          </div>
        )}
        {invoice.partner_address && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('billing.invoices.detail.partnerAddress', locale)}
            </dt>
            <dd className="text-slate-700">{invoice.partner_address}</dd>
          </div>
        )}
        {invoice.partner_vat_number && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('billing.invoices.detail.partnerVat', locale)}
            </dt>
            <dd className="text-slate-700 font-mono">{invoice.partner_vat_number}</dd>
          </div>
        )}
      </dl>

      {/* Bookings facturées */}
      {bookingIds.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('billing.invoices.detail.bookings', locale)} ({bookingIds.length})
          </div>
          <div className="text-[12px] text-slate-600 font-mono break-all">
            {bookingIds.join(', ')}
          </div>
        </div>
      )}

      {/* Line items */}
      {lineItems.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('billing.invoices.detail.lineItems', locale)} ({lineItems.length})
          </div>
          <pre className="rounded-lg bg-white border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-48">
{JSON.stringify(lineItems, null, 2)}
          </pre>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('billing.invoices.detail.notes', locale)}
          </div>
          <div className="text-[13px] text-slate-700 whitespace-pre-wrap rounded-lg bg-white border border-slate-200 p-2">
            {invoice.notes}
          </div>
        </div>
      )}
    </div>
  );
};

const BillingInvoicesTable = ({ invoices, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-billing-invoices-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('billing.invoices.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-billing-invoices-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('billing.invoices.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={FileText}
      title={t('billing.invoices.empty.title', locale)}
      desc={t(isFilterActive ? 'billing.invoices.empty.descFilter' : 'billing.invoices.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('billing.invoices.col.number',     locale),
                t('billing.invoices.col.issued',     locale),
                t('billing.invoices.col.partner',    locale),
                t('billing.invoices.col.period',     locale),
                t('billing.invoices.col.due',        locale),
                t('billing.invoices.col.total',      locale),
                t('billing.invoices.col.due_amount', locale),
                t('billing.invoices.col.status',     locale),
                t('billing.invoices.col.actions',    locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              invoices.map((inv) => {
                const key = inv.id || inv.invoice_number;
                const isExpanded = Boolean(expanded[key]);
                const statusMeta = invoiceStatusMeta[inv.status] || invoiceStatusMeta.draft;
                const isOverdue = inv.status === 'overdue';
                const detailPath = `/admin/billing/invoices/${key}`;
                const issuedDate = inv.issued_at || inv.created_at;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isOverdue && 'bg-rose-50/30'
                      )}
                      style={isOverdue ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded
                            ? t('billing.invoices.detail.hide', locale)
                            : t('billing.invoices.detail.show', locale)}
                          data-testid={`adminv3-billing-invoices-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Numéro */}
                      <td className="px-4 py-3.5">
                        <span className="inline-block rounded-lg bg-blue-50 px-2 py-1 font-mono text-[12px] font-bold text-blue-700">
                          {inv.invoice_number || '—'}
                        </span>
                      </td>

                      {/* Émise le */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatDate(issuedDate, locale)}
                      </td>

                      {/* Partenaire */}
                      <td className="px-4 py-3.5">
                        <div className="text-slate-900 font-medium text-[13px]">{inv.partner_name || '—'}</div>
                        <div className="text-[11px] text-slate-400 break-all">{inv.partner_email || ''}</div>
                      </td>

                      {/* Période */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatPeriod(inv.period_start, inv.period_end, locale)}
                      </td>

                      {/* Échéance */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatDate(inv.due_date, locale)}
                      </td>

                      {/* Total TTC */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">
                        {currency(inv.total_amount ?? inv.amount ?? 0)}
                      </td>

                      {/* Solde dû */}
                      <td className={cn(
                        'px-4 py-3.5 font-medium whitespace-nowrap',
                        (inv.amount_due ?? 0) > 0 ? 'text-rose-700' : 'text-slate-400'
                      )}>
                        {currency(inv.amount_due ?? 0)}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5">
                        <button
                          type="button"
                          onClick={() => navigate(detailPath)}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={t('billing.invoices.action.open', locale)}
                          data-testid={`adminv3-billing-invoices-open-${key}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={10} className="px-4 py-2">
                          <DetailPanel invoice={inv} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default BillingInvoicesTable;
